import { useMemo } from 'react';
import { getDraftClientPackage, getSortedFinalizedClientPackages } from '../../../utils/clientPackageUtils';
import type { ClientPackageDto, EnrichedClientDto } from '../../generated/client';
import { useClientPackagesForClient } from './use-client-packages-for-client';

export interface IUseClientPackagesResult {
    packages: ClientPackageDto[];
    sortedPackages: ClientPackageDto[];
    draftPackage?: ClientPackageDto;
    isDocInCart: (documentId: number) => boolean;
    isSuccess: boolean;
    isLoading: boolean;
    isError: boolean;
}

const emptyClientPackagesResult: IUseClientPackagesResult = {
    packages: [],
    sortedPackages: [],
    draftPackage: undefined,
    isDocInCart: () => false,
    isSuccess: false,
    isLoading: false,
    isError: false,
};

export function useClientPackages(client?: EnrichedClientDto): IUseClientPackagesResult {
    const { data: packages, isSuccess, isLoading, isError } = useClientPackagesForClient(client);

    return useMemo(() => {
        if (!isSuccess || !packages?.length) return emptyClientPackagesResult;

        const draftPackage = getDraftClientPackage(packages);
        const sortedPackages = getSortedFinalizedClientPackages(packages);

        // Add draft package at the top if existing
        if (draftPackage) sortedPackages.unshift(draftPackage);

        const isDocInCart = (documentId: number) => !!draftPackage?.clientPackageDocuments?.some((doc) => doc.documentId === documentId);

        return { packages, sortedPackages, draftPackage, isDocInCart, isSuccess, isLoading, isError };
    }, [packages, isSuccess, isLoading, isError]);
}
